import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  FileSpreadsheet,
  Loader2,
  Receipt,
  Search,
  Users,
  Wallet,
  PiggyBank,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/EmptyState";
import Pagination from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { jobStatusTone } from "@/lib/constants";
import { formatCurrency } from "@/lib/format";

// Server returns date-only strings (Europe/Istanbul calendar date): format without
// going through Date() so the browser timezone can never shift the day.
function formatIsoDate(value) {
  if (!value) return "-";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : "-";
}

function formatDays(value) {
  return value === null || value === undefined ? "-" : `${value} gün`;
}

const COLUMNS = [
  { key: "customer_name", label: "Cari / Müşteri", align: "left" },
  { key: "total_sales", label: "Toplam Satış", align: "right" },
  { key: "total_collected", label: "Toplam Tahsilat", align: "right" },
  { key: "net_balance", label: "Net Alacak", align: "right" },
  { key: "contract_date", label: "Sözleşme Tarihi", align: "left" },
  { key: "vade_gun_sayisi", label: "Vade Gün Sayısı", align: "right" },
];

function KpiCard({ icon: Icon, label, value, tone, active, onClick, clickable, testId }) {
  return (
    <Card
      className={`border transition-all ${
        clickable ? "cursor-pointer hover:shadow-md select-none" : ""
      } ${
        active
          ? "ring-2 ring-offset-1 ring-slate-900 border-slate-900 shadow-sm bg-white"
          : "border-[#DCE2E8] hover:border-slate-300"
      }`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`rounded-md p-2 shrink-0 ${tone}`}>
          <Icon size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide truncate">{label}</span>
            {clickable && active && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-800 text-white">
                Aktif
              </span>
            )}
          </div>
          <div className="text-xl font-bold text-[#171A1F] mt-0.5 truncate">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ReceivablesReportPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlMode = searchParams.get("mode");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState(urlMode === "overdue" || urlMode === "credit" ? urlMode : "open");
  const [contractFrom, setContractFrom] = useState("");
  const [contractTo, setContractTo] = useState("");
  const [sortBy, setSortBy] = useState("net_balance");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  // sync mode from URL if changed externally
  useEffect(() => {
    const qMode = searchParams.get("mode");
    if (qMode && (qMode === "open" || qMode === "credit" || qMode === "overdue") && qMode !== mode) {
      setMode(qMode);
      setPage(1);
    }
  }, [searchParams]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setPage(1);
    const newParams = new URLSearchParams(searchParams);
    newParams.set("mode", newMode);
    setSearchParams(newParams);
  };

  // debounce text search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(() => {
    const p = { mode, sort_by: sortBy, sort_dir: sortDir, page, page_size: pageSize };
    if (search) p.search = search;
    if (contractFrom) p.contract_from = contractFrom;
    if (contractTo) p.contract_to = contractTo;
    return p;
  }, [mode, search, contractFrom, contractTo, sortBy, sortDir, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/reports/receivables", { params });
      setData(res.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const resetPage = (fn) => (v) => {
    fn(v);
    setPage(1);
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortDir(key === "customer_name" || key === "contract_date" ? "asc" : "desc");
    }
    setPage(1);
  };

  const toggleExpand = (key) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setContractFrom("");
    setContractTo("");
    handleModeChange("open");
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const exportParams = {
        mode,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (search) exportParams.search = search;
      if (contractFrom) exportParams.contract_from = contractFrom;
      if (contractTo) exportParams.contract_to = contractTo;

      const response = await api.get("/reports/receivables/export", {
        params: exportParams,
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      let filename = `alacaklar_raporu_${mode}.xlsx`;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("attachment") !== -1) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(disposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, "");
        }
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel raporu başarıyla indirildi.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary;
  const items = data?.items || [];

  return (
    <div className="space-y-5" data-testid="receivables-report-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs text-slate-500">
            <Link to="/raporlar" className="hover:underline">Raporlar</Link> / Alacaklar Raporu
          </div>
          <h1 className="text-2xl font-bold text-[#171A1F]">Alacaklar Raporu</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {mode === "open"
              ? "Cari bazında açık alacak listesi. Geciken taksitleri görmek için 'Geciken Alacaklar', fazla tahsilatı olan carileri görmek için 'Fazla Tahsilat / Cari Kredisi' kartına tıklayın."
              : mode === "overdue"
              ? "Vadesi geçmiş taksiti bulunan gecikmiş alacaklar listesi. Tüm açık alacakları listelemek için 'Toplam Cari Alacağı' kartına tıklayın."
              : "Fazla tahsilatı / kredisi olan cariler listesi. Açık alacakları listelemek için 'Toplam Cari Alacağı' kartına tıklayın."}
          </p>
        </div>
      </div>

      {/* KPI cards / Mode switchers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="receivables-kpis">
        <KpiCard
          icon={Receipt}
          label="Toplam Cari Alacağı"
          value={summary ? formatCurrency(summary.total_receivable) : "-"}
          tone="bg-red-50 text-[#FF353A]"
          clickable={true}
          active={mode === "open"}
          onClick={() => handleModeChange("open")}
          testId="kpi-total-receivable"
        />
        <KpiCard
          icon={AlertCircle}
          label="Geciken Alacaklar"
          value={summary ? formatCurrency(summary.total_overdue) : "-"}
          tone="bg-rose-50 text-rose-600"
          clickable={true}
          active={mode === "overdue"}
          onClick={() => handleModeChange("overdue")}
          testId="kpi-total-overdue"
        />
        <KpiCard
          icon={PiggyBank}
          label="Fazla Tahsilat / Cari Kredisi"
          value={summary ? formatCurrency(summary.total_credit) : "-"}
          tone="bg-amber-50 text-amber-600"
          clickable={true}
          active={mode === "credit"}
          onClick={() => handleModeChange("credit")}
          testId="kpi-total-credit"
        />
        <KpiCard
          icon={Wallet}
          label="Toplam Tahsilat"
          value={summary ? formatCurrency(summary.total_collected) : "-"}
          tone="bg-emerald-50 text-emerald-600"
          clickable={false}
          testId="kpi-total-collected"
        />
        <KpiCard
          icon={Users}
          label="Açık Cari Sayısı"
          value={summary ? summary.open_customer_count.toLocaleString("tr-TR") : "-"}
          tone="bg-slate-100 text-slate-600"
          clickable={false}
          testId="kpi-open-customers"
        />
      </div>

      {/* Cleaned filter area */}
      <Card className="border-[#DCE2E8] shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-stretch md:items-end gap-3 justify-between">
            <div className="flex-1 min-w-[240px]">
              <label className="text-xs font-semibold text-slate-600 block mb-1">Cari / Müşteri</label>
              <div className="relative">
                <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Müşteri veya firma adı ara..."
                  className="pl-8 border-[#DCE2E8]"
                  data-testid="receivables-search"
                />
              </div>
            </div>
            <div className="w-full md:w-auto flex flex-wrap sm:flex-nowrap items-end gap-3">
              <div className="w-full sm:w-40">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Sözleşme Başlangıç</label>
                <Input
                  type="date"
                  value={contractFrom}
                  onChange={(e) => resetPage(setContractFrom)(e.target.value)}
                  className="border-[#DCE2E8]"
                  data-testid="receivables-date-from"
                />
              </div>
              <div className="w-full sm:w-40">
                <label className="text-xs font-semibold text-slate-600 block mb-1">Sözleşme Bitiş</label>
                <Input
                  type="date"
                  value={contractTo}
                  onChange={(e) => resetPage(setContractTo)(e.target.value)}
                  className="border-[#DCE2E8]"
                  data-testid="receivables-date-to"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={clearFilters}
                className="border-[#DCE2E8] text-slate-600 hover:text-[#171A1F]"
                data-testid="receivables-clear-filters"
              >
                Filtreleri Temizle
              </Button>
              <Button
                type="button"
                onClick={handleExportExcel}
                disabled={exporting}
                className="bg-[#171A1F] hover:bg-slate-800 text-white flex items-center gap-2"
                data-testid="receivables-export-excel"
              >
                {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
                Excel'e Aktar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <div className="rounded-md border border-[#DCE2E8] bg-white overflow-hidden shadow-sm">
        {error ? (
          <div className="flex flex-col items-center py-14 text-center" data-testid="receivables-error">
            <AlertCircle size={34} className="text-[#FF353A] mb-2" />
            <div className="font-medium text-slate-700">Rapor yüklenemedi</div>
            <div className="text-sm text-slate-500 mt-1">{String(error)}</div>
            <Button className="mt-4" variant="outline" onClick={load}>Tekrar Dene</Button>
          </div>
        ) : loading && !data ? (
          <div className="flex items-center justify-center py-16 text-slate-500" data-testid="receivables-loading">
            <Loader2 className="animate-spin mr-2" size={18} /> Yükleniyor...
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            testId="receivables-empty"
            title="Kayıt bulunamadı"
            description={
              mode === "open"
                ? "Seçili filtrelere uyan açık alacak bulunmuyor."
                : "Seçili filtrelere uyan fazla tahsilat / cari kredisi bulunmuyor."
            }
            icon={Receipt}
          />
        ) : (
          <div className={loading ? "opacity-60 transition-opacity" : ""}>
            <Table data-testid="receivables-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="w-8" />
                  {(mode === "overdue"
                    ? [
                        { key: "customer_name", label: "Cari / Müşteri", align: "left" },
                        { key: "total_sales", label: "Toplam Satış", align: "right" },
                        { key: "total_collected", label: "Toplam Tahsilat", align: "right" },
                        { key: "net_balance", label: "Net Alacak", align: "right" },
                        { key: "overdue_amount", label: "Geciken Tutar", align: "right" },
                        { key: "contract_date", label: "Sözleşme Tarihi", align: "left" },
                        { key: "vade_gun_sayisi", label: "Vade Gün Sayısı", align: "right" },
                      ]
                    : COLUMNS
                  ).map((c) => {
                    const active = sortBy === c.key;
                    const SortIcon = active ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
                    return (
                      <TableHead key={c.key} className={c.align === "right" ? "text-right" : ""}>
                        <button
                          type="button"
                          onClick={() => toggleSort(c.key)}
                          className={`inline-flex items-center gap-1 font-semibold hover:text-[#171A1F] ${active ? "text-[#171A1F]" : "text-slate-600"}`}
                          data-testid={`receivables-sort-${c.key}`}
                        >
                          {c.label}
                          <SortIcon size={13} className={active ? "" : "opacity-40"} />
                        </button>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((r) => {
                  const open = expanded.has(r.customer_key);
                  const netBal = parseFloat(r.net_balance);
                  return (
                    <Fragment key={r.customer_key}>
                      <TableRow
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => toggleExpand(r.customer_key)}
                        data-testid={`receivables-row-${r.customer_key}`}
                      >
                        <TableCell className="w-8 text-slate-400">
                          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </TableCell>
                        <TableCell className="font-medium text-[#171A1F]">
                          {r.customer_name}
                          <span className="ml-2 text-xs font-normal text-slate-400">({r.jobs.length} iş)</span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(r.total_sales)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(r.total_collected)}</TableCell>
                        <TableCell
                          className={`text-right tabular-nums font-semibold ${
                            netBal > 0 ? "text-[#FF353A]" : netBal < 0 ? "text-amber-600" : "text-slate-400"
                          }`}
                        >
                          {formatCurrency(r.net_balance)}
                        </TableCell>
                        {mode === "overdue" && (
                          <TableCell className="text-right tabular-nums font-semibold text-rose-600">
                            {formatCurrency(r.overdue_amount)}
                          </TableCell>
                        )}
                        <TableCell>{formatIsoDate(r.contract_date)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatDays(r.vade_gun_sayisi)}</TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70" data-testid={`receivables-detail-${r.customer_key}`}>
                          <TableCell />
                          <TableCell colSpan={mode === "overdue" ? 7 : 6} className="py-3">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>İş No</TableHead>
                                  <TableHead>Durum</TableHead>
                                  <TableHead>Sözleşme Tarihi</TableHead>
                                  <TableHead className="text-right">Satış Tutarı</TableHead>
                                  <TableHead className="text-right">Tahsilat</TableHead>
                                  <TableHead className="text-right">İş Bazında Bakiye</TableHead>
                                  {mode === "overdue" && <TableHead className="text-right">Geciken Tutar</TableHead>}
                                  <TableHead className="text-right">Vade Gün Sayısı</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {r.jobs.map((j) => {
                                  const bal = parseFloat(j.job_balance);
                                  return (
                                    <TableRow key={j.job_id}>
                                      <TableCell className="font-mono text-xs">
                                        <Link to={`/isler/${j.job_id}`} className="text-[#FF353A] hover:underline" onClick={(e) => e.stopPropagation()}>
                                          {j.job_number}
                                        </Link>
                                      </TableCell>
                                      <TableCell><StatusBadge tone={jobStatusTone(j.status)}>{j.status_label}</StatusBadge></TableCell>
                                      <TableCell>{formatIsoDate(j.contract_date)}</TableCell>
                                      <TableCell className="text-right tabular-nums">{formatCurrency(j.sales_amount)}</TableCell>
                                      <TableCell className="text-right tabular-nums">{formatCurrency(j.collected_amount)}</TableCell>
                                      <TableCell
                                        className={`text-right tabular-nums font-semibold ${bal < 0 ? "text-amber-600" : bal > 0 ? "text-[#171A1F]" : "text-slate-400"}`}
                                        title={bal < 0 ? "Bu işte satıştan fazla tahsilat var" : undefined}
                                      >
                                        {formatCurrency(j.job_balance)}
                                        {bal < 0 && <span className="ml-1 text-[10px] font-medium uppercase">fazla</span>}
                                      </TableCell>
                                      {mode === "overdue" && (
                                        <TableCell className="text-right tabular-nums font-semibold text-rose-600">
                                          {formatCurrency(j.overdue_amount)}
                                        </TableCell>
                                      )}
                                      <TableCell className="text-right tabular-nums">{formatDays(j.vade_gun_sayisi)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total || 0}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
